import { useOrganization } from '@clerk/react';
import type { Call as TwilioCall, Device } from '@twilio/voice-sdk';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useCallingAccess } from '@/containers/settings/calling/use-calling-access.hook';
import { apiErrorMessage } from '@/lib/appsumo-upgrade';
import {
  useCancelCallMutation,
  useGetMyNumberQuery,
  useGetVoiceTokenMutation,
  useLazyGetCallStateQuery,
  useStartCallMutation,
  type CallRecord,
} from '@/store/features/telephony.feature';
import { CallBar } from './call-bar.component';
import {
  ACTIVE_PHASES,
  CallContext,
  type ActiveCall,
  type CallApi,
  type DialRoom,
  type StartCallOptions,
} from './call-context';

class Refused extends Error {}

const SETTLE_TRIES = 5;
const SETTLE_WAIT_MS = 1000;

function micMessage(error: unknown): string {
  const name = (error as DOMException)?.name;
  if (name === 'NotFoundError' || name === 'OverconstrainedError')
    return 'No microphone found.';
  return "Microphone blocked. Allow it for this site in your browser's settings.";
}

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Places calls from the browser with Twilio's Voice SDK and keeps the one
 * live call across page changes: mounted once in the app shell, with the call
 * bar. The SDK loads on the first call, and its device is reused until the
 * org changes. Leaving the page during a call asks first (the SDK's
 * close protection); a reload ends the call, and the server's unsaved-call
 * list brings it back for wrap-up.
 */
export function CallProvider({ children }: { children: ReactNode }) {
  const { organization } = useOrganization();
  const organizationId = organization?.id ?? '';
  const access = useCallingAccess();
  const { currentData: mine } = useGetMyNumberQuery(organizationId, {
    skip: !access.enabled || !organizationId,
  });
  const available = Boolean(
    access.enabled &&
    mine?.ready &&
    mine.accountStatus === 'active' &&
    mine.number?.status === 'active'
  );

  const [getToken] = useGetVoiceTokenMutation();
  const [startCall] = useStartCallMutation();
  const [cancelCall] = useCancelCallMutation();
  const [readCall] = useLazyGetCallStateQuery();

  const [call, setCall] = useState<ActiveCall | null>(null);
  const [ended, setEnded] = useState<CallRecord | null>(null);
  const callRef = useRef<ActiveCall | null>(null);
  callRef.current = call;
  const deviceRef = useRef<Device | null>(null);
  const twilioRef = useRef<TwilioCall | null>(null);
  const finishing = useRef<string | null>(null);
  const [room, setRoom] = useState<DialRoom | null>(null);
  const roomRef = useRef<TwilioCall | null>(null);

  const update = useCallback(
    (changes: Partial<ActiveCall>) =>
      setCall((current) => (current ? { ...current, ...changes } : current)),
    []
  );

  // A new org (or calling going away) gets a new device.
  useEffect(
    () => () => {
      deviceRef.current?.destroy();
      deviceRef.current = null;
    },
    [organizationId]
  );

  const token = useCallback(async () => {
    const answer = await getToken(organizationId).unwrap();
    if (!answer.token) throw new Refused(answer.blocked?.message ?? '');
    return answer.token;
  }, [getToken, organizationId]);

  const device = useCallback(async (): Promise<Device> => {
    if (deviceRef.current) return deviceRef.current;
    const [{ Device: TwilioDevice }, first] = await Promise.all([
      import('@twilio/voice-sdk'),
      token(),
    ]);
    const created = new TwilioDevice(first, {
      closeProtection: 'A call is in progress. Leaving the page ends it.',
      logLevel: 'error',
    });
    created.on('tokenWillExpire', () => {
      token()
        .then((next) => created.updateToken(next))
        .catch(() => {
          // The next call asks for a fresh device.
          deviceRef.current = null;
        });
    });
    deviceRef.current = created;
    return created;
  }, [token]);

  const finish = useCallback(
    async (callId: string) => {
      if (finishing.current === callId) return;
      finishing.current = callId;
      twilioRef.current = null;
      update({ phase: 'ending' });
      // The server settles from Twilio's status callbacks, a moment after
      // the browser hangs up.
      let record: CallRecord | undefined;
      for (let tries = 0; tries < SETTLE_TRIES; tries += 1) {
        try {
          record = await readCall({ organizationId, id: callId }).unwrap();
          if (record.final) break;
        } catch {
          // Read again.
        }
        await wait(SETTLE_WAIT_MS);
      }
      update({ phase: 'ended', result: record });
      if (record) setEnded(record);
    },
    [organizationId, readCall, update]
  );

  const start = useCallback(
    async (options: StartCallOptions): Promise<boolean> => {
      const current = callRef.current;
      if (current && ACTIVE_PHASES.has(current.phase)) return false;
      if (!available || !organizationId) return false;
      finishing.current = null;
      setCall({ ...options, phase: 'checking', muted: false });

      // The microphone first, so a refused one never holds credits.
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        stream.getTracks().forEach((track) => track.stop());
      } catch (error) {
        update({ phase: 'failed', error: micMessage(error) });
        return false;
      }

      let callId: string;
      let rateCredits: number | undefined;
      let fromE164: string | undefined;
      try {
        const answer = await startCall({
          organizationId,
          to: options.to,
          taskId: options.taskId,
          leadId: options.leadId,
        }).unwrap();
        if (!answer.call) {
          update({
            phase: 'blocked',
            error: answer.blocked?.message ?? "The call couldn't start.",
          });
          return false;
        }
        callId = answer.call.id;
        rateCredits = answer.call.rateCredits;
        fromE164 = answer.call.fromE164;
      } catch (error) {
        update({
          phase: 'failed',
          error: apiErrorMessage(error, "The call couldn't start. Try again."),
        });
        return false;
      }

      update({ phase: 'connecting', callId, rateCredits, fromE164 });
      try {
        const placed = await (await device()).connect({ params: { callId } });
        twilioRef.current = placed;
        placed.on('ringing', () => update({ phase: 'ringing' }));
        placed.on('accept', () =>
          update({ phase: 'live', answeredAt: Date.now() })
        );
        placed.on('reconnecting', () => update({ phase: 'reconnecting' }));
        placed.on('reconnected', () => update({ phase: 'live' }));
        placed.on('mute', (muted: boolean) => update({ muted }));
        placed.on('error', (error: { message?: string }) =>
          update({ error: error?.message || 'The call dropped.' })
        );
        const done = () => void finish(callId);
        placed.on('disconnect', done);
        placed.on('cancel', done);
        placed.on('reject', done);
        return true;
      } catch (error) {
        void cancelCall({ organizationId, id: callId })
          .unwrap()
          .catch(() => {});
        update({
          phase: 'failed',
          error:
            error instanceof Refused
              ? error.message || "Calling isn't available right now."
              : "Couldn't connect the call. Check your connection and try again.",
        });
        return false;
      }
    },
    [available, organizationId, update, startCall, device, finish, cancelCall]
  );

  // Parallel dialing: the browser waits in the session's conference room;
  // leads who answer join it there.
  const joinRoom = useCallback(
    async (sessionId: string): Promise<boolean> => {
      if (!available || roomRef.current) return false;
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        stream.getTracks().forEach((track) => track.stop());
      } catch {
        return false;
      }
      setRoom({ sessionId, phase: 'connecting', muted: false });
      try {
        const placed = await (
          await device()
        ).connect({ params: { sessionId } });
        roomRef.current = placed;
        placed.on('accept', () =>
          setRoom((current) => current && { ...current, phase: 'live' })
        );
        placed.on('mute', (muted: boolean) =>
          setRoom((current) => current && { ...current, muted })
        );
        const gone = () => {
          roomRef.current = null;
          setRoom(null);
        };
        placed.on('disconnect', gone);
        placed.on('cancel', gone);
        placed.on('reject', gone);
        return true;
      } catch {
        roomRef.current = null;
        setRoom(null);
        return false;
      }
    },
    [available, device]
  );

  const api: CallApi = useMemo(
    () => ({
      available,
      call,
      ended,
      start,
      hangUp: () => {
        const current = callRef.current;
        if (twilioRef.current) {
          twilioRef.current.disconnect();
          return;
        }
        if (current?.callId && ACTIVE_PHASES.has(current.phase)) {
          void cancelCall({ organizationId, id: current.callId })
            .unwrap()
            .catch(() => {});
          void finish(current.callId);
        }
      },
      toggleMute: () => {
        const placed = twilioRef.current;
        if (placed) placed.mute(!placed.isMuted());
      },
      sendDigits: (digits) => twilioRef.current?.sendDigits(digits),
      dismiss: () => {
        if (callRef.current && ACTIVE_PHASES.has(callRef.current.phase)) return;
        setCall(null);
      },
      review: (record) =>
        setCall({
          phase: 'ended',
          to: record.toE164,
          callId: record.id,
          taskId: record.taskId ?? undefined,
          leadId: record.leadId ?? undefined,
          muted: false,
          result: record,
        }),
      finished: (record) => {
        setEnded(record);
        setCall({
          phase: 'ended',
          to: record.toE164,
          callId: record.id,
          taskId: record.taskId ?? undefined,
          leadId: record.leadId ?? undefined,
          muted: false,
          result: record,
        });
      },
      room,
      joinRoom,
      leaveRoom: () => {
        roomRef.current?.disconnect();
        roomRef.current = null;
        setRoom(null);
      },
      toggleRoomMute: () => {
        const placed = roomRef.current;
        if (placed) placed.mute(!placed.isMuted());
      },
    }),
    [
      available,
      call,
      ended,
      start,
      cancelCall,
      organizationId,
      finish,
      room,
      joinRoom,
    ]
  );

  return (
    <CallContext.Provider value={api}>
      {children}
      {(available || call) && <CallBar organizationId={organizationId} />}
    </CallContext.Provider>
  );
}
